import * as S from "./styled";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
/**
 * hooks
 */
import { usePasswordToggle } from "../../../../hooks/usePasswordToggle";
import apiRequest from "../../../../hooks/apiRequest";
import useLoading from '../../../../hooks/useLoading';
/**
 * Components
 */
import LayoutModal from "../../../../components/LayoutModal";
/**
 * Icons
 */
import { Eye, EyeSlash } from "@phosphor-icons/react";

type Inputs = {
  first_name: string;
  last_name: string;
  is_admin?: number;
  is_manager?: number;
  discount: number;
  email: string;
  password: string;
  password_confirmation: string;
};

interface ICreate {
  isOpenModal: boolean;
  setModalClose: () => void;
  refreshFrames: () => void;
}

export default function CreateUser({
  isOpenModal,
  setModalClose,
  refreshFrames,
}: ICreate) {
  const {
    register,
    control,
    reset,
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const { inputType, icon, togglePasswordVisibility } = usePasswordToggle();
  const { isLoading, startLoading, stopLoading } = useLoading();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    startLoading();
    try {
      const response = await apiRequest('user', 'POST', data);
      if (response.status === 201) {
        alert("Usuário criado com sucesso!");
        refreshFrames();
        onClose();
      }
    } catch (error: any) {
      alert(error.message || "Ocorreu um erro ao criar o usuário.");
    } finally {
      stopLoading();
    }
  };

  const onClose = () => {
    setModalClose();
    reset();
  }

  if (isOpenModal) {
    return (
      <LayoutModal
        statusLoad={isLoading}
        title="Registrar Usuário"
        subTitle="Cadastre um novo usuário em sua base de dados."
        setModalClose={onClose}
        submiteModal={handleFormSubmit(onSubmit)}
      >
        <S.Container>
          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                Nome do Usuário
                <span>*</span>
              </S.Label>
              <S.Input
                placeholder="Informe o nome"
                type="text"
                {...register("first_name", { required: true })}
              />
              {errors.first_name && (
                <S.InforErro>Campo Obrigatório</S.InforErro>
              )}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Sobrenome
                <span>*</span>
              </S.Label>
              <S.Input
                placeholder="Informe o sobrenome"
                type="text"
                {...register("last_name", { required: true })}
              />
              {errors.last_name && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>
          </S.SeparateDiv>

          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                E-mail <span>*</span>
              </S.Label>
              <S.Input
                placeholder="exemplo@gmail.com"
                type="email"
                {...register("email", { required: true })}
              />
              {errors.email && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Desconto %
              </S.Label>
              <Controller
                name="discount"
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <NumericFormat
                    className="mask"
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale={true}
                    placeholder="0,00 %"
                    suffix=" %"
                    value={value}
                    onValueChange={(values) => {
                      const purchaseValue = values.floatValue ?? 0;
                      onChange(purchaseValue); // Atualiza o valor de compra
                    }}
                    getInputRef={ref}
                  />
                )}
              />
            </S.FormInput>
          </S.SeparateDiv>

          <S.SeparateDiv>
            <S.FormInput>
              <S.Label>
                Senha <span>*</span>
              </S.Label>
              <S.ContentInput>
                <S.InputUnstyled
                  {...register("password", { required: true })}
                  type={inputType}
                  placeholder="Informe sua senha"
                />

                <S.ButtonOpenPassword
                  type="button"
                  onClick={togglePasswordVisibility}
                >
                  {icon === "eye-open" ? (
                    <Eye size={20} />
                  ) : (
                    <EyeSlash size={20} />
                  )}
                </S.ButtonOpenPassword>
              </S.ContentInput>
              {errors.password && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>

            <S.FormInput>
              <S.Label>
                Confirmar Senha <span>*</span>
              </S.Label>
              <S.ContentInput>
                <S.InputUnstyled
                  {...register("password_confirmation", { required: true })}
                  type={inputType}
                  placeholder="Confirme sua senha"
                />

                <S.ButtonOpenPassword
                  type="button"
                  onClick={togglePasswordVisibility}
                >
                  {icon === "eye-open" ? (
                    <Eye size={20} />
                  ) : (
                    <EyeSlash size={20} />
                  )}
                </S.ButtonOpenPassword>
              </S.ContentInput>
              {errors.password && <S.InforErro>Campo Obrigatório</S.InforErro>}
            </S.FormInput>
          </S.SeparateDiv>

          <S.FormInput>
            <S.Label>
              Tipo de Usuário
            </S.Label>

            <S.ContentCheckbox>
              <S.CheckBox type="checkbox" id="is_admin"  {...register('is_admin', {
                setValueAs: (value) => value ? 1 : 0
              })} />
              <S.Label htmlFor="is_admin">
                Administrador
              </S.Label>
            </S.ContentCheckbox>

            <S.ContentCheckbox>
              <S.CheckBox type="checkbox" id="is_manager" {...register('is_manager', {
                setValueAs: (value) => value ? 1 : 0
              })} />
              <S.Label htmlFor="is_manager">
                Gerente
              </S.Label>
            </S.ContentCheckbox>
            {errors.is_admin && <S.InforErro>Campo Obrigatório</S.InforErro>}
          </S.FormInput>
        </S.Container>
      </LayoutModal>
    );
  }
  return null;
}
